import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, X, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import OccasionalBuyerDialog from "@/components/OccasionalBuyerDialog";

/**
 * Combobox de clientes con búsqueda server-side (debounced).
 * - Sin preselección: `value` puede ser null hasta que el usuario elija.
 * - Muestra placeholder claro para forzar selección consciente.
 * - Búsqueda por nombre, RNC/cédula, teléfono o email vía `/customers?q=`.
 */
export default function CustomerCombobox({
  value,           // objeto cliente o null
  onChange,        // (customer|null) => void
  placeholder = "Buscar cliente…",
  required = false,
  disabled = false,
  testid = "customer-combobox",
  showClear = true,
  allowOccasional = false,  // permite facturar a un RNC/cédula sin registrarlo
}) {
  const [occOpen, setOccOpen] = useState(false);
  const [occRnc, setOccRnc] = useState("");
  const openOccasional = (rnc = "") => { setOccRnc(rnc); setOpen(false); setOccOpen(true); };
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [dgiiHint, setDgiiHint] = useState(null);  // {rnc, razon_social, estado}
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      setLoading(true);
      setDgiiHint(null);
      api.get("/customers", { params: { q: query || undefined } })
        .then(async ({ data }) => {
          const list = data || [];
          setItems(list);
          setHighlight(0);
          // Iter 77: Si no hay coincidencias y la query parece un RNC (9 dígitos),
          // intenta un lookup contra el padrón DGII para asistir al cajero.
          const clean = (query || "").replace(/[-\s]/g, "");
          if (list.length === 0 && /^\d{9}$/.test(clean)) {
            try {
              const { data: dgii } = await api.get(`/dgii/rnc/lookup/${clean}`);
              setDgiiHint(dgii);
            } catch { /* no existe */ }
          }
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const pick = (c) => {
    onChange?.(c);
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    onChange?.(null);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (items[highlight]) pick(items[highlight]); }
  };

  const empty = !value;

  return (
    <>
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-between h-10 font-normal ${empty ? "text-muted-foreground" : "text-foreground"} ${required && empty ? "border-destructive" : ""}`}
          data-testid={testid}
        >
          <span className="truncate">
            {value?.occasional ? (
              <>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 mr-1.5 uppercase" data-testid={`${testid}-occasional-badge`}>Ocasional</span>
                <span className="font-semibold">{value.name}</span>
                <span className="ml-1 text-xs text-muted-foreground">· {value.rnc_cedula}</span>
              </>
            ) : value ? (
              <>
                {value.code && (
                  <span className="text-primary font-bold mr-1.5 data-cell">{value.code}</span>
                )}
                {value.commercial_name ? (
                  <>
                    <span className="font-semibold">{value.commercial_name}</span>
                    <span className="ml-1 text-xs text-muted-foreground">
                      ({value.name})
                    </span>
                  </>
                ) : (
                  <span className="font-semibold">{value.name}</span>
                )}
                {value.rnc_cedula && (
                  <span className="ml-1 text-xs text-muted-foreground">· {value.rnc_cedula}</span>
                )}
              </>
            ) : placeholder}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {value && showClear && (
              <span
                role="button"
                aria-label="Limpiar cliente"
                onPointerDown={clear}
                onClick={(e) => e.stopPropagation()}
                className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted opacity-60 hover:opacity-100 cursor-pointer"
                data-testid={`${testid}-clear`}
              >
                <X className="w-4 h-4" />
              </span>
            )}
            <ChevronsUpDown className="w-4 h-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-none" align="start">
        <div className="p-2 border-b border-border">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Código, nombre, RNC/cédula, teléfono o email…"
              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
              data-testid={`${testid}-search`}
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto" data-testid={`${testid}-list`}>
          {loading && (
            <div className="p-3 text-center text-xs text-muted-foreground">Buscando…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Sin resultados. Prueba con otro término.
            </div>
          )}
          {!loading && dgiiHint && items.length === 0 && (
            <div className="p-3 bg-info/10 border-b border-info/30 text-xs flex items-start gap-2"
                 data-testid={`${testid}-dgii-hint`}>
              <div className="w-5 h-5 rounded-full bg-info text-white flex items-center justify-center shrink-0 text-[10px] font-bold">
                D
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-foreground truncate">
                  {dgiiHint.razon_social}
                </div>
                <div className="text-muted-foreground">
                  RNC {dgiiHint.rnc} · {dgiiHint.estado || "ACTIVO"} · {dgiiHint.regimen || "—"}
                </div>
                <div className="mt-1 text-info">
                  Encontrado en padrón DGII pero no está registrado como cliente.
                  {allowOccasional ? " Puedes facturarle como cliente ocasional." : " Crea el cliente en Clientes → Nuevo o pide al admin."}
                </div>
                {allowOccasional && (
                  <button type="button" onClick={() => openOccasional(dgiiHint.rnc)}
                    className="mt-1.5 text-xs font-semibold text-primary underline" data-testid={`${testid}-dgii-occasional`}>
                    Facturar a {dgiiHint.rnc} como ocasional
                  </button>
                )}
              </div>
            </div>
          )}
          {!loading && items.map((c, i) => (
            <button
              key={c.id}
              type="button"
              onClick={() => pick(c)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left p-2.5 flex items-start gap-2 border-b border-border/40 last:border-b-0 ${
                i === highlight ? "bg-accent" : "hover:bg-accent/60"
              }`}
              data-testid={`${testid}-option-${c.id}`}
            >
              <Check className={`w-4 h-4 mt-0.5 shrink-0 ${value?.id === c.id ? "opacity-100 text-primary" : "opacity-0"}`} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                  {c.code && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0 data-cell">{c.code}</span>
                  )}
                  {c.commercial_name ? (
                    <>
                      <span className="truncate">{c.commercial_name}</span>
                      <span className="text-xs text-muted-foreground font-normal truncate">
                        ({c.name})
                      </span>
                    </>
                  ) : (
                    <span className="truncate">{c.name}</span>
                  )}
                  {c.is_default && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-primary/20 text-primary uppercase shrink-0">
                      Default
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground flex gap-2 flex-wrap">
                  {c.rnc_cedula && <span className="data-cell">RNC {c.rnc_cedula}</span>}
                  {c.phone && <span className="data-cell">{c.phone}</span>}
                  {c.email && <span className="truncate">{c.email}</span>}
                </div>
                {(c.saldo_inicial_cxc || 0) > 0 && (
                  <div className="text-[10px] mt-0.5 text-orange-700 dark:text-orange-400 font-bold">
                    Con saldo migrado
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        {allowOccasional && (
          <button type="button" onClick={() => openOccasional(/^[\d-\s]+$/.test(query) ? query : "")}
            className="w-full flex items-center gap-2 p-2.5 border-t border-border text-sm font-semibold text-primary hover:bg-accent/60"
            data-testid={`${testid}-occasional-btn`}>
            <UserPlus className="w-4 h-4" /> Cliente ocasional con RNC o cédula
          </button>
        )}
      </PopoverContent>
    </Popover>
    {allowOccasional && (
      <OccasionalBuyerDialog open={occOpen} onOpenChange={setOccOpen} initialRnc={occRnc}
        initial={value?.occasional && !occRnc ? value : null} onConfirm={(b) => onChange?.(b)} />
    )}
    </>
  );
}
